import { ApolloQueryResult } from '@apollo/client';
import {
  Accordion,
  Button,
  Divider,
  Flex,
  Link,
  MenuTrigger,
  Notice,
  Progress,
  Stack,
  Text,
  TextArea,
  Truncate,
} from '@eleveo/components-react';
import { ChatIcon, CheckmarkIcon, CrossIcon, FilterIcon, QueueIcon, SegmentsIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { parseAbsolute } from '@internationalized/date';
import { pathOr } from 'ramda';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { useConfig } from '../../app/ConfigurationProvider';
import { RequestStatusComponent, RequestSubtypeComponent } from '../../components/AgentsRequests';
import { DateFormatter } from '../../components/DateFormatter';
import { Sidebar, SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import { Center } from '../../components/LayoutNext/components/Center';
import { ModalConfirmationNewStyle as ModalConfirmation } from '../../components/ModalConfirmationNewStyle';
import { max255chars } from '../../components/MySchedule/validations';
import { REQUESTS_PROPS, REQUEST_STATE_PROPS } from '../../config/agentsRequests';
import {
  GetSupervisorPtoRequestQuery,
  PtoRequest,
  PtoRequestState,
  Queue,
  RequestState,
  RequestSubtype,
  useGetAgentSchedulesInfoQuery,
  useGetSupervisorPtoRequestQuery,
} from '../../graphql/components';
import { PATH_SCHEDULING_STUDIO_EDIT } from '../../router/routes';
import { formatSecondsToReadableForm } from '../../utils/dateRange';
import { DateRangeFilter } from '../../utils/filter';
import { formatNameOfPerson } from '../../utils/helpers';
import { RequestsSidebarFilterState } from './RequestsFilterSidebar';

type RequestsSidebarPTODetailProps = {
  handleCancel: () => void;
  handleResolve: (
    fromState: RequestState,
    toState: RequestState,
    refetchPtoRequestQuery: () => Promise<ApolloQueryResult<GetSupervisorPtoRequestQuery>>,
    message?: string
  ) => Promise<void>;
  handleQuickFilter: (quickFilterValues: RequestsSidebarFilterState) => void;
  requestId: string;
  queues: Record<string, Queue>;
};

export type DeclineApprovedRequestModal =
  | { state: 'ready' }
  | {
      request: PtoRequest;
      state: 'open';
    };

export const RequestsSidebarPTODetail: React.FC<RequestsSidebarPTODetailProps> = ({
  handleCancel,
  handleResolve,
  handleQuickFilter,
  requestId,
  queues,
}) => {
  const { t } = useTranslation();

  const { appUrl } = useConfig();
  const { userId, timezone } = useAuth();

  const [declineApprovedRequestModal, setDeclineApprovedRequestModal] = useState<DeclineApprovedRequestModal>({
    state: 'ready',
  });

  const [messageForRequester, setMessageForRequester] = useState<string | undefined>(undefined);

  const messageForRequesterErrorMessage = useMemo(() => {
    const errorMessage = max255chars(messageForRequester);

    if (!errorMessage) {
      return undefined;
    }

    return t(errorMessage, { characters: 255 });
  }, [messageForRequester, t]);

  const {
    data: ptoRequestData,
    loading: ptoRequestLoading,
    error: ptoRequestError,
    refetch: refetchPtoRequestQuery,
  } = useGetSupervisorPtoRequestQuery({
    fetchPolicy: 'network-only',
    variables: {
      ptoRequestId: requestId,
      supervisorId: userId,
    },
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: ({ supervisorPtoRequest }) => {
      // When no request found, BE replies with 403 which is not internally treated as error
      if (!supervisorPtoRequest) {
        handleCancel();
      }
    },
  });

  const {
    data: agentSchedulesData,
    loading: agentSchedulesLoading,
    error: agentSchedulesError,
  } = useGetAgentSchedulesInfoQuery({
    fetchPolicy: 'network-only',
    skip: !ptoRequestData?.supervisorPtoRequest,
    variables: {
      agentId: ptoRequestData?.supervisorPtoRequest?.agent.id ?? '',
      startDateTime: ptoRequestData?.supervisorPtoRequest?.dateRange.startDateTime ?? undefined,
      endDateTime: ptoRequestData?.supervisorPtoRequest?.dateRange.endDateTime ?? undefined,
    },
  });

  const loading = ptoRequestLoading || agentSchedulesLoading;
  const error = ptoRequestError || agentSchedulesError;

  const request = ptoRequestData?.supervisorPtoRequest;

  const skipRendering = (loading && !request) || error;

  const checkValidationBeforeResolveAction = async (newStatus: PtoRequestState) => {
    if (messageForRequesterErrorMessage || !request) {
      return;
    }

    try {
      await handleResolve(
        request.state as unknown as RequestState,
        newStatus as unknown as RequestState,
        refetchPtoRequestQuery,
        messageForRequester
      );

      // clean-up message for requester value after decline/approval
      setMessageForRequester(undefined);
    } catch (err) {
      // `customFetch()` will display error message

      const errorCode = pathOr(undefined, ['networkError', 'result', 'errorCode'], err);

      // there could be various state inconsistencies, however the message should be purged for all of them
      if (errorCode?.match(/fe.sch.pto.request.state.transition..*.unsupported/)) {
        setMessageForRequester(undefined);
      }
    }
  };

  const requestQueueItems = useMemo(() => {
    const filteredQueues = request?.agent.queueIds.map((queueId) => queues[queueId]).filter(Boolean) ?? [];

    return [
      {
        id: 'queueIds',
        title: t('queue.queues'),
        icon: <QueueIcon />,
        content:
          filteredQueues.length > 0 ? (
            <Stack direction="vertical">
              {filteredQueues.map(({ name, id }) => (
                <Text className="font-bold break-words overflow-auto" key={id}>
                  {name}
                </Text>
              ))}
            </Stack>
          ) : (
            <Flex column alignItems="center">
              <Text>{t('queue.noQueues')}</Text>
            </Flex>
          ),
      },
    ];
  }, [request, queues, t]);

  const requesterSchedules = useMemo(() => {
    return (
      agentSchedulesData?.agentSchedulesInfo.map(({ id, name }) => ({
        id,
        href: `${appUrl}${PATH_SCHEDULING_STUDIO_EDIT}/${id}`,
        target: '_blank',
        text: name,
        testId: `requests:request-sidebar:requester-schedules:${id}`,
      })) ?? []
    );
  }, [agentSchedulesData?.agentSchedulesInfo, appUrl]);

  const onDeclineApprovedRequestModalCancel = () => {
    setDeclineApprovedRequestModal({
      state: 'ready',
    });
  };

  const onDeclineApprovedRequestModalSubmit = async () => {
    if (declineApprovedRequestModal.state === 'ready') {
      return;
    }

    checkValidationBeforeResolveAction(PtoRequestState.Declined);
    onDeclineApprovedRequestModalCancel();
  };

  return (
    <Sidebar onClose={handleCancel} title={t('schedule.request.requestDetail')}>
      <>
        {loading && (
          <Center className="h-screen">
            <Progress />
          </Center>
        )}

        {!skipRendering && request && (
          <>
            {[PtoRequestState.PendingApproval, PtoRequestState.Approved].includes(request.state) && (
              <SidebarPanel>
                <Stack className="w-full">
                  <Button
                    className="mr-15"
                    onClick={() =>
                      request.state === PtoRequestState.Approved
                        ? setDeclineApprovedRequestModal({
                            request: request as PtoRequest,
                            state: 'open',
                          })
                        : checkValidationBeforeResolveAction(PtoRequestState.Declined)
                    }
                    color="error"
                    testId="requests:request-sidebar:button-decline"
                  >
                    <CrossIcon />
                    {t('layout.action.decline')}
                  </Button>

                  {request.state === PtoRequestState.PendingApproval && (
                    <Button
                      color="success"
                      type="button"
                      onClick={() => checkValidationBeforeResolveAction(PtoRequestState.Approved)}
                      testId="requests:request-sidebar:button-approve"
                    >
                      <CheckmarkIcon />
                      {t('layout.action.approve')}
                    </Button>
                  )}
                </Stack>
              </SidebarPanel>
            )}

            <SidebarContent>
              <SidebarPart>
                <Stack direction="vertical" className="w-full px-20 pt-10 pb-15">
                  <Flex alignItems="baseline" justifyContent="between" className="w-full" gap={[10, 0]} wrap>
                    <Flex alignItems="center" className="max-w-full" wrap gap={[10, 0]}>
                      <span className="text-sm">{t('form.status')}:</span>
                      <RequestStatusComponent
                        status={request.state as unknown as RequestState}
                        testId="requests:request-sidebar:status"
                      />
                    </Flex>

                    <Flex
                      justifyContent="end"
                      className="text-right text-sm flex-1 wrapper__tiny-column-gap"
                      testId="requests:request-sidebar:createdAt-timestamp"
                      wrap
                    >
                      {t('form.created')}:
                      <DateFormatter
                        className="font-bold"
                        options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                      >
                        {new Date(request.createdAt)}
                      </DateFormatter>
                    </Flex>
                  </Flex>
                  <Flex className="w-full" column>
                    <Flex alignItems="baseline" justifyContent="between" className="w-full" gap={[10, 0]} wrap>
                      <Flex alignItems="center" className="max-w-full" wrap gap={[10, 0]}>
                        <span className="text-sm">{t('schedule.request.requester')}:</span>
                        <strong data-test-id="requests:request-sidebar:requester">
                          {formatNameOfPerson(request.agent.firstName, request.agent.lastName)}
                        </strong>
                      </Flex>
                      {requesterSchedules.length > 0 && (
                        <Flex justifyContent="end" className="flex-1" wrap>
                          <MenuTrigger items={requesterSchedules}>
                            <Button isSmall testId="requests:request-sidebar:requester-schedules" variant="outline">
                              <SegmentsIcon />
                              {t('schedule.schedule', { count: 2 })}
                            </Button>
                          </MenuTrigger>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                  {requesterSchedules.length === 0 && (
                    <Notice testId="requests:request-sidebar:requester-no-schedules">
                      {t('schedule.noScheduleForDateRange')}
                    </Notice>
                  )}
                </Stack>

                <Accordion items={requestQueueItems} expansionMode="single" testId="requests:request-sidebar:queues">
                  {(item) => <div className="px-20 pt-10 pb-10">{item.content}</div>}
                </Accordion>

                <Divider />

                <div className="px-20 pt-10 pb-15">
                  <RequestSubtypeComponent
                    type={request.type as unknown as RequestSubtype}
                    testId="requests:request-sidebar:subtype"
                  />
                  <Flex className="w-full" column>
                    <div className="mr-auto mt-10 text-sm">
                      <Flex className="gap-5" testId="requests:request-sidebar:request-start-date-time-timestamp">
                        <span className="mr-auto">{t('time.start')}:</span>
                        <DateFormatter
                          className="font-bold"
                          options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                        >
                          {new Date(request.dateRange.startDateTime)}
                        </DateFormatter>
                      </Flex>
                      <Flex className="gap-5" testId="requests-sidebar:request-end-date-time-timestamp">
                        <span className="mr-auto">{t('time.end')}:</span>
                        <DateFormatter
                          className="font-bold"
                          options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                        >
                          {new Date(request.dateRange.endDateTime)}
                        </DateFormatter>
                      </Flex>
                      <Flex className="gap-5" testId="requests:request-sidebar:request-total-duration">
                        <span className="mr-auto">{t('time.total')}:</span>
                        <p className="font-bold">
                          {formatSecondsToReadableForm(
                            Math.floor(
                              (new Date(request.dateRange.endDateTime).getTime() -
                                new Date(request.dateRange.startDateTime).getTime()) /
                                1000
                            ),
                            t,
                            true
                          )}
                        </p>
                      </Flex>
                    </div>
                  </Flex>

                  <Flex justifyContent="end">
                    <Button
                      className="ml-auto"
                      isSmall
                      onClick={() =>
                        handleQuickFilter({
                          requestedRange: {
                            start: parseAbsolute(request.dateRange.startDateTime, timezone),
                            end: parseAbsolute(request.dateRange.endDateTime, timezone),
                          },
                          createdRange: null as unknown as DateRangeFilter,
                          queues: request?.agent.queueIds ?? [],
                          states: ['APPROVED', 'PENDING_APPROVAL'],
                          subtypes: [RequestSubtype.Sick, RequestSubtype.Vacation],
                        })
                      }
                      variant="outline"
                      testId="requests:request-sidebar:button-concurrent-requests"
                    >
                      <FilterIcon />
                      {t('schedule.request.concurrentRequests')}
                    </Button>
                  </Flex>

                  {/* Audits information part */}
                  {request.audits.map(({ message: auditMessage, toState, who, when }, index) => {
                    if (toState === RequestState.PendingApproval && !auditMessage) {
                      return null;
                    }

                    return (
                      <React.Fragment key={`${toState}-${when}`}>
                        <Divider className="mt-10" />
                        <Stack
                          className="w-full"
                          direction="vertical"
                          spacing={0}
                          testId={`requests:request-sidebar:request-audit-log-${index}`}
                        >
                          {toState !== RequestState.PendingApproval && (
                            <Stack className="mt-10 text-sm">
                              <Truncate>
                                <span data-test-id={`requests:request-sidebar:request-audit-log:to-state-${index}`}>
                                  {t(REQUEST_STATE_PROPS[toState].nameKey)}:{' '}
                                </span>
                                <span
                                  className="font-bold"
                                  data-test-id={`requests:request-sidebar:request-audit-log:person-${index}`}
                                >
                                  {formatNameOfPerson(who.firstName, who.lastName)}
                                </span>
                              </Truncate>
                              <DateFormatter
                                className="flex-none font-bold ml-auto"
                                options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                              >
                                {new Date(when)}
                              </DateFormatter>
                            </Stack>
                          )}
                          {auditMessage && (
                            <Flex
                              className="mt-10"
                              testId={`requests:request-sidebar:request-audit-log:message-${index}`}
                            >
                              <ChatIcon className="text-blue-700 text-lg" />
                              <span className="ml-10 break-words overflow-auto">{auditMessage}</span>
                            </Flex>
                          )}
                        </Stack>
                      </React.Fragment>
                    );
                  })}

                  {request.state === PtoRequestState.PendingApproval && (
                    <>
                      <Divider className="mt-10 mb-15" />
                      <TextArea
                        className="w-full"
                        errorMessage={messageForRequesterErrorMessage}
                        label={t('schedule.request.messageForRequester')}
                        onChange={(message) => setMessageForRequester(message.length ? message : undefined)}
                        testId="requests:request-sidebar:supervisor:message"
                        validationState={messageForRequesterErrorMessage ? 'invalid' : undefined}
                        value={messageForRequester ?? ''}
                      />
                    </>
                  )}
                </div>
              </SidebarPart>
            </SidebarContent>

            <ModalConfirmation
              acceptCaption={t('layout.action.confirm')}
              cancelCaption={t('layout.action.cancel')}
              isOpen={declineApprovedRequestModal.state === 'open'}
              onAccept={onDeclineApprovedRequestModalSubmit}
              onCancel={onDeclineApprovedRequestModalCancel}
              title={`${t('layout.action.decline')} ${t(REQUESTS_PROPS[request.type].nameKey)}`}
            >
              {declineApprovedRequestModal.state === 'open' && (
                <>
                  <p>{t('schedule.request.declineApprovedRequestModalBody')}</p>
                  <p className="pt-10">{t('schedule.request.affectedSchedules')}</p>
                  <Stack as="ul" className="pb-15 mis-15 list-disc" direction="vertical">
                    {requesterSchedules.length > 0 ? (
                      requesterSchedules?.map(({ id, href, target, text }) => (
                        <li key={id}>
                          <Link
                            testId={`requests:request-sidebar:decline-modal:affected-schedule(${id}):link`}
                            href={href}
                            target={target}
                          >
                            {text}
                          </Link>
                        </li>
                      ))
                    ) : (
                      <li key="noAffectedSchedule">{t('schedule.request.noAffectedSchedules')}</li>
                    )}
                  </Stack>
                  <TextArea
                    className="w-full py-15"
                    errorMessage={messageForRequesterErrorMessage}
                    label={t('schedule.request.messageForRequester')}
                    onChange={(message) => setMessageForRequester(message.length ? message : undefined)}
                    testId="requests:request-sidebar:decline-modal:supervisor:message"
                    validationState={messageForRequesterErrorMessage ? 'invalid' : undefined}
                    value={messageForRequester || ''}
                  />
                  <p>{t('schedule.request.declineApprovedRequestModalBody2')}</p>
                </>
              )}
            </ModalConfirmation>
          </>
        )}
      </>
    </Sidebar>
  );
};
